import { useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import { getMissingFields, updateDetails } from "../../../API/litigationApis";

// Define the required fields map outside the component
const REQUIRED_FIELDS_MAP = {
     "summary_suit": [
          "npa_date", "sanction_date", "application_date", "agreement_date",
          "incorpration_date", "lrn_date", "outstanding_date_as_on", "address1",
          "roi_percentage", "product_name", "coborrower1_address", "guarantor_address",
          "loan_amount"
     ],
     "comm_suit": [
          "npa_date", "sanction_date", "application_date", "agreement_date",
          "incorpration_date", "lrn_date", "outstanding_date_as_on", "address1",
          "roi_percentage", "product_name", "coborrower1_address", "guarantor_address",
          "loan_amount"
     ],
     "pre_ep": [
          "npa_date", "sanction_date", "application_date", "agreement_date",
          "incorpration_date", "lrn_date", "outstanding_date_as_on", "product_name"
     ],
     "mediation_application": [
          "npa_date", "sanction_date", "application_date", "agreement_date",
          "incorpration_date", "lrn_date", "outstanding_date_as_on", "address1",
          "product_name", "roi_percentage", "branch_address", "loan_amount"
     ],
     "interim_application": [
          "npa_date", "sanction_date", "application_date", "agreement_date",
          "incorpration_date", "lrn_date", "outstanding_date_as_on", "product_name",
          "coborrower1_address", "guarantor_address"
     ],
     "loan_recall_notice": [
          "address1", "branch_address", "loan_amount"
     ],
     "original_application": [
          "lan_number_2", "loan_amount_2", "claim_amount_2", "roi_percentage_2",
          "npa_date_2", "sanction_date_2", "agreement_date_2", "product_name_2",
          "tribunal_type",
          "demand_notice_date", "evaluated_price", "plot_unit", "description_of_security",
          "type_of_security", "asset_type", "borrower_type", "borrower_pan",
          "city", "state", "taluka", "bank_account_no"
     ],
     "sec_95": [] // Assuming no specific fields listed for sec_95
};


const CasePdfsModal = ({ show, onHide, casePdfs, caseId }) => {
     const [showNewModal, setShowNewModal] = useState(false);
     const [formData, setFormData] = useState({});
     const [missingFields, setMissingFields] = useState([]);
     const [selectedPdfType, setSelectedPdfType] = useState("");
     const [showWarningModal, setShowWarningModal] = useState(false);

     const getDownloadUrl = (pdfUrl) =>
          `https://api.themedius.ai/dashboard/api/download_media/?url=${encodeURIComponent(
               pdfUrl
          )}`;


     const pdfTypes = [
          { label: "Summary Suit", value: "summary_suit" },
          { label: "Commercial Suit", value: "comm_suit" },
          { label: "Execution Petition", value: "pre_ep" },
          { label: "Mediation Application", value: "mediation_application" },
          { label: "Interim Application", value: "interim_application" },
          { label: "Loan Recall Notice", value: "loan_recall_notice" },
          { label: "Original Application", value: "original_application" },
          { label: "Sec 95", value: "sec_95" }
     ];

     const pdfMap = {};
     (casePdfs || []).forEach((pdf) => {
          pdfMap[pdf.pdf_type] = pdf;
     });

     const handleChange = (field, value) => {
          setFormData((prev) => ({ ...prev, [field]: value }));
     };

     const handleGenerate = async (pdfType) => {
          try {
               setSelectedPdfType(pdfType);

               const res = await getMissingFields({ case_id: caseId, pdf_type: pdfType });

               const missing = res?.data || [];
               setMissingFields(missing);
               //setShowWarningModal(true); true only if there are missing fields
               if (missing.length > 0) {
               setShowWarningModal(true);
               } else {
                    setShowNewModal(true);
               }
              
          } catch (error) {
               console.error("Error fetching missing data:", error);
          }
     };

     const generateAndDownloadPdf = async (pdfType) => {
          try {
               const payload = {
                    case_id: caseId,
                    category: pdfType,
               };

               const response = await updateDetails(payload);
               console.log("PDF Generation Response:", response);

               // Check multiple possible response formats for PDF URL
               let pdfUrl = null;
               if (response?.data) {
                    pdfUrl = response.data;
               } else if (response?.pdf_url) {
                    pdfUrl = response.pdf_url;
               } else if (response?.url) {
                    pdfUrl = response.url;
               }

               if (pdfUrl) {
                    console.log("Downloading PDF from URL:", pdfUrl);
                    // Download the PDF from S3 URL
                    await downloadPdfFromUrl(pdfUrl, `${pdfType}_${caseId}.pdf`);

                    // Store modal state before reload
                    localStorage.setItem('reopenCasePaperModal', JSON.stringify({
                         caseId: caseId,
                         timestamp: Date.now()
                    }));

                    // Reload the page after download
                    setTimeout(() => {
                         window.location.reload();
                    }, 2000); // Increased delay to ensure download completes
               } else {
                    console.error("No PDF URL found in response:", response);
                    alert("PDF generated but download URL not found. Please refresh and try viewing the PDF.");
                    // Still reload to show updated PDF list
                    setTimeout(() => {
                         window.location.reload();
                    }, 1000);
               }
          } catch (error) {
               console.error("Error generating PDF:", error);
               alert("Failed to generate PDF. Please try again.");
          }
     };

     const downloadPdfFromUrl = async (pdfUrl, fileName) => {
          try {
               console.log("Starting PDF download for:", pdfUrl);
               const downloadUrl = getDownloadUrl(pdfUrl);
               console.log("Download URL:", downloadUrl);

               const response = await fetch(downloadUrl);
               console.log("Fetch response status:", response.status);

               if (!response.ok) {
                    throw new Error(`Network response was not ok: ${response.status}`);
               }

               const blob = await response.blob();
               console.log("Blob size:", blob.size);

               if (blob.size === 0) {
                    throw new Error('Downloaded file is empty');
               }

               const url = window.URL.createObjectURL(blob);
               const link = document.createElement('a');
               link.href = url;
               link.download = fileName || 'document.pdf';
               link.style.display = 'none';
               document.body.appendChild(link);

               // Force click with a small delay
               setTimeout(() => {
                    link.click();
                    console.log("Download triggered for:", fileName);

                    // Clean up after a delay
                    setTimeout(() => {
                         document.body.removeChild(link);
                         window.URL.revokeObjectURL(url);
                    }, 100);
               }, 100);

          } catch (error) {
               console.error('Error downloading PDF:', error);
               alert(`Failed to download PDF: ${error.message}`);
               throw error; // Re-throw to handle in calling function
          }
     };

     const handleSubmit = async () => {
          try {
               const payload = {
                    case_id: caseId,
                    category: selectedPdfType,
                    ...formData,
               };

               const response = await updateDetails(payload);
               console.log("PDF Generation Response (handleSubmit):", response);

               // Check multiple possible response formats for PDF URL
               let pdfUrl = null;
               if (response?.data) {
                    pdfUrl = response.data;
               } else if (response?.pdf_url) {
                    pdfUrl = response.pdf_url;
               } else if (response?.url) {
                    pdfUrl = response.url;
               }

               if (pdfUrl) {
                    console.log("Downloading PDF from URL (handleSubmit):", pdfUrl);
                    // Download the PDF from S3 URL
                    await downloadPdfFromUrl(pdfUrl, `${selectedPdfType}_${caseId}.pdf`);

                    // Store modal state before reload
                    localStorage.setItem('reopenCasePaperModal', JSON.stringify({
                         caseId: caseId,
                         timestamp: Date.now()
                    }));

                    setShowNewModal(false);

                    // Reload the page after download
                    setTimeout(() => {
                         window.location.reload();
                    }, 2000); // Increased delay to ensure download completes
               } else {
                    console.error("No PDF URL found in response (handleSubmit):", response);
                    alert("PDF generated but download URL not found. Please refresh and try viewing the PDF.");
                    setShowNewModal(false);
                    // Still reload to show updated PDF list
                    setTimeout(() => {
                         window.location.reload();
                    }, 1000);
               }
          } catch (error) {
               console.error("Error submitting form:", error);
               alert("Failed to generate PDF. Please try again.");
          }
     };

     return (
          <>
               {/* Main Case Papers Modal */}
               <Modal show={show} onHide={onHide} size="md">
                    <Modal.Header closeButton>
                         <Modal.Title>Case Papers</Modal.Title>
                    </Modal.Header>
                    <Modal.Body style={{ margin: "20px 0px" }}>
                         <ul className="list-group">
                              {pdfTypes.map((type) => {
                                   const pdf = pdfMap[type.value];
                                   return (
                                        <li
                                             key={type.value}
                                             className="list-group-item d-flex justify-content-between align-items-center"
                                        >
                                             <span>{type.label}</span>
                                             {pdf ? (
                                                  <div className="d-flex align-items-center gap-1">
                                                       <button
                                                            className="btn btn-sm btn-warning"
                                                            onClick={() => handleGenerate(type.value)}
                                                       >
                                                            Regenerate
                                                       </button>
                                                       <a
                                                            href={getDownloadUrl(pdf.pdf_url)}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="btn btn-sm btn-primary"
                                                       >
                                                            View
                                                       </a>
                                                  </div>
                                             ) : (
                                                  <button
                                                       className="btn btn-sm btn-primary"
                                                       onClick={() => handleGenerate(type.value)}
                                                  >
                                                       Generate
                                                  </button>
                                             )}
                                        </li>
                                   );
                              })}
                         </ul>
                    </Modal.Body>
               </Modal>

               {/* Warning Modal for Missing Fields */}
               <Modal show={showWarningModal} onHide={() => setShowWarningModal(false)} centered>
                    <Modal.Header closeButton>
                         <Modal.Title className="text-warning">Warning: Missing Details</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                         <p><strong>WARNING:</strong> {missingFields.length} required detail(s) are missing to generate the '{pdfTypes.find(p => p.value === selectedPdfType)?.label}'.</p>
                         <p>If the client has shared documents, please click "UPDATE ADDITIONAL DETAILS" from the Action menu before proceeding.</p>
                    </Modal.Body>
                    <Modal.Footer>
                         <Button variant="secondary" onClick={() => { setShowWarningModal(false); setShowNewModal(true); }}>Cancel & Update</Button>
                         <Button variant="danger" onClick={() => { setShowWarningModal(false); generateAndDownloadPdf(selectedPdfType); }}>Generate Incomplete</Button>
                    </Modal.Footer>
               </Modal>

               {/* Fill Missing Fields Modal */}
               <Modal
                    show={showNewModal}
                    onHide={() => {
                         setShowNewModal(false);
                         setSelectedPdfType("");
                    }}
                    size="lg"
               >
                    <Modal.Header closeButton>
                         <Modal.Title>Fill Missing Fields</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                         <div className="row">
                              {/* Address 1 (Added to support required fields) */}
                              {missingFields.includes("address1") && (
                                   <Form.Group className="mt-3 col-md-4">
                                        <Form.Label>Address 1</Form.Label>
                                        <Form.Control
                                             as="textarea"
                                             placeholder="Enter Primary Address"
                                             rows={1}
                                             value={formData.address1 || ""}
                                             onChange={(e) => handleChange("address1", e.target.value)}
                                        />
                                   </Form.Group>
                              )}
                              {/* LRN Status */}
                              {missingFields.includes("lrn_status") && (
                                   <Form.Group className="mt-3 col-md-4">
                                        <Form.Label>LRN Status</Form.Label>
                                        <Form.Control
                                             type="text"
                                             placeholder="Enter LRN Status"
                                             value={formData.lrn_status || ""}
                                             onChange={(e) => handleChange("lrn_status", e.target.value)}
                                        />
                                   </Form.Group>
                              )}
                              {/* LRN Date */}
                              {missingFields.includes("lrn_date") && (
                                   <Form.Group className="mt-3 col-md-4">
                                        <Form.Label>LRN Date</Form.Label>
                                        <Form.Control
                                             type="date"
                                             value={formData.lrn_date || ""}
                                             onChange={(e) => handleChange("lrn_date", e.target.value)}
                                        />
                                   </Form.Group>
                              )}
                              {/* Address 3 */}
                              {missingFields.includes("address3") && (
                                   <Form.Group className="mt-3 col-md-4">
                                        <Form.Label>Address 3</Form.Label>
                                        <Form.Control
                                             type="text"
                                             placeholder="Enter Address 3"
                                             value={formData.address3 || ""}
                                             onChange={(e) => handleChange("address3", e.target.value)}
                                        />
                                   </Form.Group>
                              )}
                              {/* Address 4 */}
                              {missingFields.includes("address4") && (
                                   <Form.Group className="mt-3 col-md-4">
                                        <Form.Label>Address 4</Form.Label>
                                        <Form.Control
                                             type="text"
                                             placeholder="Enter Address 4"
                                             value={formData.address4 || ""}
                                             onChange={(e) => handleChange("address4", e.target.value)}
                                        />
                                   </Form.Group>
                              )}
                              {/* Branch Address */}
                              {missingFields.includes("branch_address") && (
                                   <Form.Group className="mt-3 col-md-4">
                                        <Form.Label>Branch Address</Form.Label>
                                        <Form.Control
                                             type="text"
                                             placeholder="Enter Branch Address"
                                             value={formData.branch_address || ""}
                                             onChange={(e) =>
                                                  handleChange("branch_address", e.target.value)
                                             }
                                        />
                                   </Form.Group>
                              )}
                              {/* Email (Borrower Email) */}
                              {missingFields.includes("email") && (
                                   <Form.Group className="mt-3 col-md-4">
                                        <Form.Label>Email</Form.Label>
                                        <Form.Control
                                             type="email"
                                             placeholder="Enter Email"
                                             value={formData.email || ""}
                                             onChange={(e) => handleChange("email", e.target.value)}
                                        />
                                   </Form.Group>
                              )}
                              {/* Branch Email */}
                              {missingFields.includes("branch_email") && (
                                   <Form.Group className="mt-3 col-md-4">
                                        <Form.Label>Branch Email</Form.Label>
                                        <Form.Control
                                             type="email"
                                             placeholder="Enter Branch Email"
                                             value={formData.branch_email || ""}
                                             onChange={(e) => handleChange("branch_email", e.target.value)}
                                        />
                                   </Form.Group>
                              )}
                              {/* Customer Mobile Number */}
                              {missingFields.includes("customer_mobile_number") && (
                                   <Form.Group className="mt-3 col-md-4">
                                        <Form.Label>Customer Mobile Number</Form.Label>
                                        <Form.Control
                                             type="text"
                                             placeholder="Enter Customer Mobile Number"
                                             value={formData.customer_mobile_number || ""}
                                             onChange={(e) =>
                                                  handleChange("customer_mobile_number", e.target.value)
                                             }
                                        />
                                   </Form.Group>
                              )}
                              {/* NPA Date */}
                              {missingFields.includes("npa_date") && (
                                   <Form.Group className="mt-3 col-md-4">
                                        <Form.Label>NPA Date</Form.Label>
                                        <Form.Control
                                             type="date"
                                             value={formData.npa_date || ""}
                                             onChange={(e) => handleChange("npa_date", e.target.value)}
                                        />
                                   </Form.Group>
                              )}
                              {/* Sanction Date */}
                              {missingFields.includes("sanction_date") && (
                                   <Form.Group className="mt-3 col-md-4">
                                        <Form.Label>Sanction Date</Form.Label>
                                        <Form.Control
                                             type="date"
                                             value={formData.sanction_date || ""}
                                             onChange={(e) => handleChange("sanction_date", e.target.value)}
                                        />
                                   </Form.Group>
                              )}
                              {/* Agreement Date */}
                              {missingFields.includes("agreement_date") && (
                                   <Form.Group className="mt-3 col-md-4">
                                        <Form.Label>Agreement Date</Form.Label>
                                        <Form.Control
                                             type="date"
                                             value={formData.agreement_date || ""}
                                             onChange={(e) =>
                                                  handleChange("agreement_date", e.target.value)
                                             }
                                        />
                                   </Form.Group>
                              )}
                              {/* Application Date */}
                              {missingFields.includes("application_date") && (
                                   <Form.Group className="mt-3 col-md-4">
                                        <Form.Label>Application Date</Form.Label>
                                        <Form.Control
                                             type="date"
                                             value={formData.application_date || ""}
                                             onChange={(e) =>
                                                  handleChange("application_date", e.target.value)
                                             }
                                        />
                                   </Form.Group>
                              )}
                              {/* Incorporation Date */}
                              {missingFields.includes("incorpration_date") && (
                                   <Form.Group className="mt-3 col-md-4">
                                        <Form.Label>Incorporation Date</Form.Label>
                                        <Form.Control
                                             type="date"
                                             value={formData.incorpration_date || ""}
                                             onChange={(e) =>
                                                  handleChange("incorpration_date", e.target.value)
                                             }
                                        />
                                   </Form.Group>
                              )}
                              {/* Loan Amount (Total Loan Amount) */}
                              {missingFields.includes("loan_amount") && (
                                   <Form.Group className="mt-3 col-md-4">
                                        <Form.Label>Loan Amount</Form.Label>
                                        <Form.Control
                                             type="number"
                                             placeholder="Enter Loan Amount"
                                             value={formData.loan_amount || ""}
                                             onChange={(e) => handleChange("loan_amount", e.target.value)}
                                        />
                                   </Form.Group>
                              )}
                              {/* Disbursal Date */}
                              {missingFields.includes("disbursal_date") && (
                                   <Form.Group className="mt-3 col-md-4">
                                        <Form.Label>Disbursal Date</Form.Label>
                                        <Form.Control
                                             type="date"
                                             value={formData.disbursal_date || ""}
                                             onChange={(e) =>
                                                  handleChange("disbursal_date", e.target.value)
                                             }
                                        />
                                   </Form.Group>
                              )}
                              {/* Product Group */}
                              {missingFields.includes("product_group") && (
                                   <Form.Group className="mt-3 col-md-4">
                                        <Form.Label>Product Group</Form.Label>
                                        <Form.Control
                                             type="text"
                                             placeholder="Enter Product Group"
                                             value={formData.product_group || ""}
                                             onChange={(e) =>
                                                  handleChange("product_group", e.target.value)
                                             }
                                        />
                                   </Form.Group>
                              )}
                              {/* Product Name */}
                              {missingFields.includes("product_name") && (
                                   <Form.Group className="mt-3 col-md-4">
                                        <Form.Label>Product Name</Form.Label>
                                        <Form.Control
                                             type="text"
                                             placeholder="Enter Product Name"
                                             value={formData.product_name || ""}
                                             onChange={(e) => handleChange("product_name", e.target.value)}
                                        />
                                   </Form.Group>
                              )}
                              {/* Arbitrator Name */}
                              {missingFields.includes("arbitrator_name") && (
                                   <Form.Group className="mt-3 col-md-4">
                                        <Form.Label>Arbitrator Name</Form.Label>
                                        <Form.Control
                                             type="text"
                                             placeholder="Enter Arbitrator Name"
                                             value={formData.arbitrator_name || ""}
                                             onChange={(e) =>
                                                  handleChange("arbitrator_name", e.target.value)
                                             }
                                        />
                                   </Form.Group>
                              )}
                              {/* City */}
                              {missingFields.includes("city") && (
                                   <Form.Group className="mt-3 col-md-4">
                                        <Form.Label>City</Form.Label>
                                        <Form.Control
                                             type="text"
                                             placeholder="Enter City"
                                             value={formData.city || ""}
                                             onChange={(e) => handleChange("city", e.target.value)}
                                        />
                                   </Form.Group>
                              )}
                              {/* State */}
                              {missingFields.includes("state") && (
                                   <Form.Group className="mt-3 col-md-4">
                                        <Form.Label>State</Form.Label>
                                        <Form.Control
                                             type="text"
                                             placeholder="Enter State"
                                             value={formData.state || ""}
                                             onChange={(e) => handleChange("state", e.target.value)}
                                        />
                                   </Form.Group>
                              )}
                              {/* Zip Code */}
                              {missingFields.includes("zip_code") && (
                                   <Form.Group className="mt-3 col-md-4">
                                        <Form.Label>Zip Code</Form.Label>
                                        <Form.Control
                                             type="text"
                                             placeholder="Enter ZIP Code"
                                             value={formData.zip_code || ""}
                                             onChange={(e) => handleChange("zip_code", e.target.value)}
                                        />
                                   </Form.Group>
                              )}
                              {/* Bank Account No */}
                              {missingFields.includes("bank_account_no") && (
                                   <Form.Group className="mt-3 col-md-4">
                                        <Form.Label>Bank Account No</Form.Label>
                                        <Form.Control
                                             type="text"
                                             placeholder="Enter Bank Account Number"
                                             value={formData.bank_account_no || ""}
                                             onChange={(e) =>
                                                  handleChange("bank_account_no", e.target.value)
                                             }
                                        />
                                   </Form.Group>
                              )}
                              {/* POS */}
                              {missingFields.includes("pos") && (
                                   <Form.Group className="mt-3 col-md-4">
                                        <Form.Label>POS</Form.Label>
                                        <Form.Control
                                             type="text" placeholder="Enter POS"
                                             value={formData.pos || ""}
                                             onChange={(e) => handleChange("pos", e.target.value)}
                                        />
                                   </Form.Group>
                              )}
                              {/* EMI Amount */}
                              {missingFields.includes("emi_amount") && (
                                   <Form.Group className="mt-3 col-md-4">
                                        <Form.Label>EMI Amount</Form.Label>
                                        <Form.Control
                                             type="number"
                                             placeholder="Enter EMI Number"
                                             value={formData.emi_amount || ""}
                                             onChange={(e) => handleChange("emi_amount", e.target.value)}
                                        />
                                   </Form.Group>
                              )}
                              {/* WhatsApp Delivered Status */}
                              {missingFields.includes("whatsapp_delivered_status") && (
                                   <Form.Group className="mt-3 col-md-4">
                                        <Form.Label>WhatsApp Delivered Status</Form.Label>
                                        <Form.Control
                                             type="text"
                                             placeholder="Enter Whatsapp Delivery Status"
                                             value={formData.whatsapp_delivered_status || ""}
                                             onChange={(e) =>
                                                  handleChange("whatsapp_delivered_status", e.target.value)
                                             }
                                        />
                                   </Form.Group>
                              )}
                              {/* SMS Delivered Status */}
                              {missingFields.includes("sms_delivered_status") && (
                                   <Form.Group className="mt-3 col-md-4">
                                        <Form.Label>SMS Delivered Status</Form.Label>
                                        <Form.Control
                                             type="text"
                                             placeholder="Enter SMS Delivery Status"
                                             value={formData.sms_delivered_status || ""}
                                             onChange={(e) =>
                                                  handleChange("sms_delivered_status", e.target.value)
                                             }
                                        />
                                   </Form.Group>
                              )}
                              {/* Email Delivered Status */}
                              {missingFields.includes("email_delivered_status") && (
                                   <Form.Group className="mt-3 col-md-4">
                                        <Form.Label>Email Delivered Status</Form.Label>
                                        <Form.Control
                                             type="text"
                                             placeholder="Enter Email Delivery Status"
                                             value={formData.email_delivered_status || ""}
                                             onChange={(e) =>
                                                  handleChange("email_delivered_status", e.target.value)
                                             }
                                        />
                                   </Form.Group>
                              )}
                              {/* Case Date */}
                              {missingFields.includes("case_date") && (
                                   <Form.Group className="mt-3 col-md-4">
                                        <Form.Label>Case Date</Form.Label>
                                        <Form.Control
                                             type="date"
                                             value={formData.case_date || ""}
                                             onChange={(e) => handleChange("case_date", e.target.value)}
                                        />
                                   </Form.Group>
                              )}
                              {/* Case Shorten URL */}
                              {missingFields.includes("case_shorten_url") && (
                                   <Form.Group className="mt-3 col-md-4">
                                        <Form.Label>Case Shorten URL</Form.Label>
                                        <Form.Control
                                             type="text"
                                             placeholder="Enter Case Shorten URL"
                                             value={formData.case_shorten_url || ""}
                                             onChange={(e) =>
                                                  handleChange("case_shorten_url", e.target.value)
                                             }
                                        />
                                   </Form.Group>
                              )}
                              {/* Proprietor Name */}
                              {missingFields.includes("proprietor_name") && (
                                   <Form.Group className="mt-3 col-md-4">
                                        <Form.Label>Proprietor Name</Form.Label>
                                        <Form.Control
                                             type="text"
                                             placeholder="Enter Proprietor Name"
                                             value={formData.proprietor_name || ""}
                                             onChange={(e) =>
                                                  handleChange("proprietor_name", e.target.value)
                                             }
                                        />
                                   </Form.Group>
                              )}
                              {/* Co-Borrower Name */}
                              {missingFields.includes("coborrower1_name") && (
                                   <Form.Group className="mt-3 col-md-4">
                                        <Form.Label>Co-Borrower Name</Form.Label>
                                        <Form.Control
                                             type="text"
                                             placeholder="Enter Co-Borrower Name"
                                             value={formData.coborrower1_name || ""}
                                             onChange={(e) =>
                                                  handleChange("coborrower1_name", e.target.value)
                                             }
                                        />
                                   </Form.Group>
                              )}
                              {/* Product Details */}
                              {missingFields.includes("product_details") && (
                                   <Form.Group className="mt-3 col-md-4">
                                        <Form.Label>Product Details</Form.Label>
                                        <Form.Control
                                             type="text"
                                             placeholder="Enter Product Details"
                                             value={formData.product_details || ""}
                                             onChange={(e) =>
                                                  handleChange("product_details", e.target.value)
                                             }
                                        />
                                   </Form.Group>
                              )}
                              {/* Guarantor Name */}
                              {missingFields.includes("guarantor_name") && (
                                   <Form.Group className="mt-3 col-md-4">
                                        <Form.Label>Guarantor Name</Form.Label>
                                        <Form.Control
                                             type="text"
                                             placeholder="Enter Gurantor Name"
                                             value={formData.guarantor_name || ""}
                                             onChange={(e) =>
                                                  handleChange("guarantor_name", e.target.value)
                                             }
                                        />
                                   </Form.Group>
                              )}
                              {/* Guarantor Address */}
                              {missingFields.includes("guarantor_address") && (
                                   <Form.Group className="mt-3 col-md-4">
                                        <Form.Label>Guarantor Address</Form.Label>
                                        <Form.Control
                                             as="textarea"
                                             placeholder="Enter Guarantor Address"
                                             rows={1}
                                             value={formData.guarantor_address || ""}
                                             onChange={(e) =>
                                                  handleChange("guarantor_address", e.target.value)
                                             }
                                        />
                                   </Form.Group>
                              )}
                              {/* Proprietor Address */}
                              {missingFields.includes("proprietor_address") && (
                                   <Form.Group className="mt-3 col-md-4">
                                        <Form.Label>Proprietor Address</Form.Label>
                                        <Form.Control
                                             as="textarea"
                                             placeholder="Enter Propreitor Address"
                                             rows={1}
                                             value={formData.proprietor_address || ""}
                                             onChange={(e) =>
                                                  handleChange("proprietor_address", e.target.value)
                                             }
                                        />
                                   </Form.Group>
                              )}
                              {/* Co-Borrower Address */}
                              {missingFields.includes("coborrower1_address") && (
                                   <Form.Group className="mt-3 col-md-4 mb-3">
                                        <Form.Label>Co-Borrower Address</Form.Label>
                                        <Form.Control
                                             as="textarea"
                                             placeholder="Enter Co-Borrower Address"
                                             rows={1}
                                             value={formData.coborrower1_address || ""}
                                             onChange={(e) =>
                                                  handleChange("coborrower1_address", e.target.value)
                                             }
                                        />
                                   </Form.Group>
                              )}
                              {/* Outstanding Date as on */}
                              {missingFields.includes("outstanding_date_as_on") && (
                                   <Form.Group className="mt-3 col-md-4">
                                        <Form.Label>Outstanding Date as on</Form.Label>
                                        <Form.Control
                                             type="date"
                                             value={formData.outstanding_date_as_on || ""}
                                             onChange={(e) =>
                                                  handleChange("outstanding_date_as_on", e.target.value)
                                             }
                                        />   
                                   </Form.Group>
                              )}

                              {missingFields.includes("roi_percentage") && (
                                   <Form.Group className="mt-3 col-md-4">
                                        <Form.Label>ROI Percentage</Form.Label>
                                        <Form.Control
                                             type="text"
                                             placeholder="Enter ROI Percentage"
                                             value={formData.roi_percentage || ""}
                                             onChange={(e) => handleChange("roi_percentage", e.target.value)}
                                        />
                                   </Form.Group>
                              )}
                               {/* "lan_number_2", "loan_amount_2", "claim_amount_2", "roi_percentage_2",
          "npa_date_2", "sanction_date_2", "agreement_date_2", "product_name_2",
          "tribunal_type", */}
                              {missingFields.includes("lan_number_2") && (
                                   <Form.Group className="mt-3 col-md-4">
                                        <Form.Label>LAN Number 2</Form.Label>
                                        <Form.Control
                                             type="text"
                                             placeholder="Enter LAN Number 2"
                                             value={formData.lan_number_2 || ""}
                                             onChange={(e) => handleChange("lan_number_2", e.target.value)}
                                        />
                                   </Form.Group>
                              )}
                              {missingFields.includes("loan_amount_2") && (
                                   <Form.Group className="mt-3 col-md-4">
                                        <Form.Label>Loan Amount 2</Form.Label>
                                        <Form.Control
                                             type="number"
                                             placeholder="Enter Loan Amount 2"
                                             value={formData.loan_amount_2 || ""}
                                             onChange={(e) => handleChange("loan_amount_2", e.target.value)}
                                        />
                                   </Form.Group>
                              )}
                              {missingFields.includes("claim_amount_2") && ( 
                                   <Form.Group className="mt-3 col-md-4">
                                        <Form.Label>Claim Amount 2</Form.Label>
                                        <Form.Control
                                             type="number"
                                             placeholder="Enter Claim Amount 2"
                                             value={formData.claim_amount_2 || ""}
                                             onChange={(e) => handleChange("claim_amount_2", e.target.value)}
                                        />
                                   </Form.Group>
                              )}
                              {missingFields.includes("roi_percentage_2") && (
                                   <Form.Group className="mt-3 col-md-4">
                                        <Form.Label>ROI Percentage 2</Form.Label>
                                        <Form.Control
                                             type="text"
                                             placeholder="Enter ROI Percentage 2"
                                             value={formData.roi_percentage_2 || ""}
                                             onChange={(e) => handleChange("roi_percentage_2", e.target.value)}
                                        />
                                   </Form.Group>
                              )}
                              {missingFields.includes("npa_date_2") && (
                                   <Form.Group className="mt-3 col-md-4">
                                        <Form.Label>NPA Date 2</Form.Label>
                                        <Form.Control
                                             type="date"
                                             value={formData.npa_date_2 || ""}
                                             onChange={(e) => handleChange("npa_date_2", e.target.value)}
                                        />
                                   </Form.Group>
                              )}
                              {missingFields.includes("sanction_date_2") && (
                                   <Form.Group className="mt-3 col-md-4">
                                        <Form.Label>Sanction Date 2</Form.Label>
                                        <Form.Control
                                             type="date"
                                             value={formData.sanction_date_2 || ""}
                                             onChange={(e) => handleChange("sanction_date_2", e.target.value)}
                                        />
                                   </Form.Group>
                              )}
                              {missingFields.includes("agreement_date_2") && (
                                   <Form.Group className="mt-3 col-md-4">
                                        <Form.Label>Agreement Date 2</Form.Label>
                                        <Form.Control
                                             type="date"
                                             value={formData.agreement_date_2 || ""}
                                             onChange={(e) => handleChange("agreement_date_2", e.target.value)}
                                        />
                                   </Form.Group>
                              )}
                              {missingFields.includes("product_name_2") && (
                                   <Form.Group className="mt-3 col-md-4">
                                        <Form.Label>Product Name 2</Form.Label>
                                        <Form.Control
                                             type="text"
                                             placeholder="Enter Product Name 2"
                                             value={formData.product_name_2 || ""}
                                             onChange={(e) => handleChange("product_name_2", e.target.value)}
                                        />
                                   </Form.Group>
                              )}
                              {missingFields.includes("tribunal_type") && (
                                   <Form.Group className="mt-3 col-md-4">
                                        <Form.Label>Tribunal Type</Form.Label>
                                        <Form.Control
                                             type="text"
                                             placeholder="Enter Tribunal Type"
                                             value={formData.tribunal_type || ""}
                                             onChange={(e) => handleChange("tribunal_type", e.target.value)}
                                        />
                                   </Form.Group>
                              )}
                              {missingFields.includes("demand_notice_date") && (
                                   <Form.Group className="mt-3 col-md-4">
                                        <Form.Label>Demand Notice Date</Form.Label>
                                        <Form.Control
                                             type="date"
                                             value={formData.demand_notice_date || ""}
                                             onChange={(e) => handleChange("demand_notice_date", e.target.value)}
                                        />
                                   </Form.Group>
                              )}
                              {missingFields.includes("evaluated_price") && (
                                   <Form.Group className="mt-3 col-md-4">
                                        <Form.Label>Evaluated Price</Form.Label>
                                        <Form.Control
                                             type="number"
                                             placeholder="Enter Evaluated Price"
                                             value={formData.evaluated_price || ""}
                                             onChange={(e) => handleChange("evaluated_price", e.target.value)}
                                        />
                                   </Form.Group>
                              )}
                              {missingFields.includes("plot_unit") && (
                                   <Form.Group className="mt-3 col-md-4">
                                        <Form.Label>Plot Unit</Form.Label>
                                        <Form.Control
                                             type="text"
                                             placeholder="Enter Plot Unit"
                                             value={formData.plot_unit || ""}
                                             onChange={(e) => handleChange("plot_unit", e.target.value)}
                                        />
                                   </Form.Group>
                              )}
                              {missingFields.includes("description_of_security") && (
                                   <Form.Group className="mt-3 col-md-4">
                                        <Form.Label>Description of Security</Form.Label>
                                        <Form.Control
                                             type="text"
                                             placeholder="Enter Description of Security"
                                             value={formData.description_of_security || ""}
                                             onChange={(e) =>
                                                  handleChange("description_of_security", e.target.value)
                                             }
                                        />
                                   </Form.Group>
                              )}
                              {missingFields.includes("type_of_security") && (
                                   <Form.Group className="mt-3 col-md-4">
                                        <Form.Label>Type of Security</Form.Label>
                                        <Form.Control
                                             type="text"
                                             placeholder="Enter Type of Security"
                                             value={formData.type_of_security || ""}
                                             onChange={(e) =>
                                                  handleChange("type_of_security", e.target.value)
                                             }
                                        />
                                   </Form.Group>
                              )}
                              {missingFields.includes("asset_type") && (
                                   <Form.Group className="mt-3 col-md-4">
                                        <Form.Label>Asset Type</Form.Label>
                                        <Form.Control
                                             type="text"
                                             placeholder="Enter Asset Type"
                                             value={formData.asset_type || ""}
                                             onChange={(e) => handleChange("asset_type", e.target.value)}
                                        />
                                   </Form.Group>
                              )}
                              {missingFields.includes("borrower_type") && (
                                   <Form.Group className="mt-3 col-md-4">
                                        <Form.Label>Borrower Type</Form.Label>
                                        <Form.Control
                                             type="text"
                                             placeholder="Enter Borrower Type"
                                             value={formData.borrower_type || ""}
                                             onChange={(e) => handleChange("borrower_type", e.target.value)}
                                        />
                                   </Form.Group>
                              )}
                              {missingFields.includes("borrower_pan") && (
                                   <Form.Group className="mt-3 col-md-4">
                                        <Form.Label>Borrower PAN</Form.Label>
                                        <Form.Control
                                             type="text"
                                             placeholder="Enter Borrower PAN"
                                             value={formData.borrower_pan || ""}
                                             onChange={(e) => handleChange("borrower_pan", e.target.value)}
                                        />
                                   </Form.Group>
                              )}
                              {missingFields.includes("taluka") && (
                                   <Form.Group className="mt-3 col-md-4">
                                        <Form.Label>Taluka</Form.Label>
                                        <Form.Control
                                             type="text"
                                             placeholder="Enter Taluka"
                                             value={formData.taluka || ""}
                                             onChange={(e) => handleChange("taluka", e.target.value)}
                                        />
                                   </Form.Group>
                              )}
                              {missingFields.includes("bank_account_no") && (
                                   <Form.Group className="mt-3 col-md-4">
                                        <Form.Label>Bank Account No</Form.Label>
                                        <Form.Control
                                             type="text"
                                             placeholder="Enter Bank Account No"
                                             value={formData.bank_account_no || ""}
                                             onChange={(e) => handleChange("bank_account_no", e.target.value)}
                                        />
                                   </Form.Group>
                              )}
                              {missingFields.includes("state") && (
                                   <Form.Group className="mt-3 col-md-4">
                                        <Form.Label>State</Form.Label>
                                        <Form.Control
                                             type="text"
                                             placeholder="Enter State"
                                             value={formData.state || ""}
                                             onChange={(e) => handleChange("state", e.target.value)}
                                        />
                                   </Form.Group>
                              )}
                              {missingFields.includes("city") && (
                                   <Form.Group className="mt-3 col-md-4">
                                        <Form.Label>City</Form.Label>
                                        <Form.Control
                                             type="text"
                                             placeholder="Enter City"
                                             value={formData.city || ""}
                                             onChange={(e) => handleChange("city", e.target.value)}
                                        />
                                   </Form.Group>
                              )}
                              {missingFields.includes("claim_amount") && (
                                   <Form.Group className="mt-3 col-md-4">
                                        <Form.Label>Claim Amount</Form.Label>
                                        <Form.Control
                                             type="number"
                                             placeholder="Enter Claim Amount"
                                             value={formData.claim_amount || ""}    
                                             onChange={(e) => handleChange("claim_amount", e.target.value)}
                                        />
                                   </Form.Group>
                              )}
                         </div>
                    </Modal.Body>
                    <Modal.Footer>
                         <Button variant="secondary" onClick={() => setShowNewModal(false)}>
                              Cancel
                         </Button>
                         <Button variant="primary" onClick={handleSubmit}>
                              Submit & Generate
                         </Button>
                    </Modal.Footer>
               </Modal>
          </>
     );
};

export default CasePdfsModal;